"use client"

import {
  Anchor,
  Ship,
  Globe,
  Users,
  Target,
  Lightbulb,
  Heart,
  Leaf,
  Award,
  TrendingUp,
  Handshake,
  Calendar,
  MapPin,
  ExternalLink,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import PartnersSection from "../components/PartnersSection"
import SponsorBanner from "../components/SponsorBanner"

export default function AboutUs() {
  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0)

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)
  }, [])

  // Real sponsors data
  const sponsors = {
    about: [
      {
        name: "Ministry of Transport & Logistics",
        logo: "/assets/images/MoTL.jpg",
        tagline: "Building Ethiopia's Future",
        description: "Developing transportation infrastructure and logistics policies.",
        website: "https://www.motl.gov.et/",
        category: "Government",
      },
      {
        name: "Ethiopian Maritime Training Institute",
        logo: "/assets/images/emtisc.jpg",
        tagline: "Training Tomorrow's Leaders",
        description: "World-class maritime education and professional training programs.",
        website: "https://emticorp.com/",
        category: "Education",
      },
    ],
  }

  // Auto-rotate sponsors for dynamic ads
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSponsorIndex((prev) => (prev + 1) % sponsors.about.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [sponsors.about.length])

  const coreValues = [
    {
      icon: <Leaf className="h-8 w-8" />,
      title: "Sustainability",
      description: "Promoting environmentally responsible maritime practices for future generations.",
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Inclusivity",
      description: "Creating opportunities for diverse talent across all maritime sectors.",
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: "Innovation",
      description: "Driving technological advancement and creative solutions in maritime industries.",
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Empowerment",
      description: "Building capacity and confidence in Africa's maritime workforce.",
    },
  ]

  const timeline = [
    {
      year: "2020",
      title: "Conference Inception",
      description: "Vision established to create Africa's premier maritime talent platform",
    },
    {
      year: "2021",
      title: "First Virtual Event",
      description: "Successfully launched with 500+ participants across 15 African countries",
    },
    {
      year: "2022",
      title: "Hybrid Format",
      description: "Expanded to hybrid model, reaching 1,200+ maritime professionals",
    },
    {
      year: "2023",
      title: "Regional Partnerships",
      description: "Established partnerships with major African maritime organizations",
    },
    {
      year: "2024",
      title: "Skills Development",
      description: "Launched comprehensive maritime skills development programs",
    },
    {
      year: "2025",
      title: "Addis Ababa Summit",
      description: "Hosting our largest in-person conference in Ethiopia's capital",
    },
  ]

  const organizers = [
    {
      name: "African Maritime Development Fund",
      description: "Leading maritime development initiatives across Africa",
      logo: "assets/images/MAsset 9@3x-8.png",
    },
    {
      name: "Ethiopian Maritime Authority",
      description: "Government partner supporting maritime excellence",
      logo: "assets/images/MAsset 9@3x-8.png",
    },
    {
      name: "Pan-African Maritime Institute",
      description: "Educational excellence in maritime training",
      logo: "assets/images/MAsset 9@3x-8.png",
    },
    {
      name: "Blue Economy Alliance",
      description: "Sustainable ocean economy development",
      logo: "assets/images/MAsset 9@3x-8.png",
    },
  ]

  const teamMembers = [
    {
      name: "Dr. Amara Okafor",
      role: "Conference Director",
      bio: "20+ years in maritime policy and African development",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
    },
    {
      name: "Captain Yohannes Tadesse",
      role: "Technical Advisor",
      bio: "Former Ethiopian Maritime Authority Director",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
    },
    {
      name: "Ms. Fatima Al-Rashid",
      role: "Industry Relations",
      bio: "Expert in maritime talent development and partnerships",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face",
    },
  ]

  // Dynamic sponsor ad component
  const SponsorAd = ({ sponsor, size = "medium", className = "", showTagline = true }) => {
    const getCategoryColor = (category) => {
      switch (category) {
        case "Government":
          return "from-blue-600 to-blue-800"
        case "Education":
          return "from-green-600 to-emerald-700"
        case "Events":
          return "from-purple-600 to-indigo-700"
        default:
          return "from-gray-600 to-gray-800"
      }
    }
    console.log(sponsor)

    const sizeClasses = {
      small: "h-24",
      medium: "h-32",
      large: "h-48",
    }

    return (
      <div
        className={`bg-gradient-to-r ${getCategoryColor(sponsor.category)} rounded-lg shadow-lg overflow-hidden ${className}`}
      >
        <div className="bg-black/20 px-3 py-1 text-xs text-white/80 text-center border-b border-white/10 flex items-center justify-between">
          <span>Sponsored by {sponsor.category}</span>
          <a
            href={sponsor.website}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className={`flex items-center justify-center p-4 ${sizeClasses[size]}`}>
          <div className="text-center text-white w-full">
            <div className="flex items-center justify-center mb-3">
              <img
                src={sponsor.logo || "/placeholder.svg"}
                alt={sponsor.name}
                className="h-8 w-auto max-w-[120px] object-contain bg-white/90 rounded px-2 py-1"
              />
            </div>
            <h3 className="font-semibold text-sm mb-1">{sponsor.name}</h3>
            {showTagline && <p className="text-xs text-white/80 mb-2">{sponsor.tagline}</p>}
            <p className="text-xs text-white/70 line-clamp-2">{sponsor.description}</p>
          </div>
        </div>
      </div>
    )
  }

  // Interactive sponsor card for inline ads
  const InteractiveSponsorCard = ({ sponsor, className = "" }) => {
    const getCategoryColor = (category) => {
      switch (category) {
        case "Government":
          return "from-blue-600 to-blue-800"
        case "Education":
          return "from-green-600 to-emerald-700"
        case "Events":
          return "from-purple-600 to-indigo-700"
        default:
          return "from-gray-600 to-gray-800"
      }
    }

    return (
      <div
        className={`bg-gradient-to-r ${getCategoryColor(sponsor.category)} rounded-lg shadow-lg p-6 transform hover:scale-105 transition-all duration-300 ${className}`}
      >
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-white/80 bg-white/20 px-2 py-1 rounded">Official Partner</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/60">{sponsor.category}</span>
            <a
              href={sponsor.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <img
            src={sponsor.logo || "/placeholder.svg"}
            alt={sponsor.name}
            className="h-12 w-auto max-w-[80px] object-contain bg-white/90 rounded p-2"
          />
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg mb-1">{sponsor.name}</h3>
            <p className="text-white/80 text-sm">{sponsor.tagline}</p>
          </div>
        </div>

        <p className="text-white/90 text-sm mb-4">{sponsor.description}</p>

        <div className="flex items-center justify-between">
          <a
            href={sponsor.website}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/20 hover:bg-white/30 text-white text-sm px-4 py-2 rounded transition-colors flex items-center gap-2"
          >
            Learn More
            <ExternalLink className="h-3 w-3" />
          </a>
          <div className="text-white/60 text-xs">Official Conference Partner</div>
        </div>
      </div>
    )
  }

  const currentSponsor = sponsors.about[currentSponsorIndex]

  return (
    <section className="min-h-screen bg-white" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
      {/* Hero Section */}
      <div className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <section className="absolute inset-0 bg-gradient-to-r from-primary-navy via-primary-blue to-primary-navy">
          <img
            src="/assets/images/aboutimage.jpeg"
            alt="African maritime heritage"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        </section>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-primary-navy/40 rounded-full backdrop-blur-sm border-2 border-[#A65C4A]/30">
              <Anchor className="h-12 w-12 text-[#A65C4A]" />
            </div>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">About Africa Maritime Conference</h1>
          <p className="text-xl md:text-2xl text-white/90 font-light">
            Empowering Maritime Excellence Across the Continent
          </p>
          <div className="mt-8 inline-flex items-center justify-center gap-4 text-white bg-primary-navy/40 backdrop-blur-sm py-3 px-6 rounded-full">
            <MapPin className="h-5 w-5 text-primary-orange" />
            <span>Addis Ababa, Ethiopia</span>
            <Calendar className="h-5 w-5 ml-4 text-primary-orange" />
            <span>October 14-16, 2025</span>
          </div>
        </div>
      </div>

      {/* Top Banner Ad - Rotating Sponsor */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <SponsorAd sponsor={currentSponsor} size="medium" />
      </div>

      {/* Main content with responsive sidebar layout */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Main content */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            {/* Mission & Vision */}
            <section className="py-12 sm:py-20 px-4" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
              <div className="max-w-6xl mx-auto">
                <div className="grid md:grid-cols-2 gap-8 sm:gap-16 items-center">
                  <div
                    className="bg-black rounded-lg shadow-lg p-6 sm:p-8 border border-primary-navy/10"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 rounded-full bg-primary-navy/10">
                        <Target className="h-8 w-8 text-primary-navy" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-primary-navy">Our Mission</h2>
                    </div>
                    <div className="h-1 w-20 bg-gradient-to-r from-primary-orange to-primary-blue mb-6"></div>
                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6">
                      To create a transformative platform that connects, educates, and empowers maritime professionals
                      across Africa, fostering innovation and sustainable growth in the blue economy.
                    </p>
                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                      We believe in the immense potential of Africa's maritime sector and are committed to building
                      bridges between talent, technology, and opportunity.
                    </p>
                  </div>

                  <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 border border-primary-navy/10">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 rounded-full bg-primary-navy/10">
                        <Globe className="h-8 w-8 text-primary-navy" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-primary-navy">Our Vision</h2>
                    </div>
                    <div className="h-1 w-20 bg-gradient-to-r from-primary-orange to-primary-blue mb-6"></div>
                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6">
                      To establish Africa as a global leader in maritime excellence, where every coastal and landlocked
                      nation contributes to and benefits from a thriving, sustainable maritime ecosystem.
                    </p>
                    <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                      We envision a future where African maritime talent drives innovation, sustainability, and economic
                      prosperity across the continent and beyond.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Inline Ad - Ministry of Transport & Logistics */}
            <div className="px-4 mb-8">
              <InteractiveSponsorCard sponsor={sponsors.about[0]} />
            </div>

            {/* Partners Section */}
            <PartnersSection />

            {/* Core Values */}
            <section className="py-12 sm:py-20 bg-gray-50">
              <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">Our Core Values</h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-primary-orange to-primary-blue mx-auto mb-6"></div>
                  <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                    The principles that guide our mission to transform Africa's maritime landscape
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                  {coreValues.map((value, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg overflow-hidden shadow-md border-b-4 border-[#A65C4A] hover:shadow-lg transition-shadow duration-300"
                    >
                      <div className="p-6 text-center">
                        <div className="flex justify-center mb-5">
                          <div className="p-4 rounded-full bg-gray-100 text-[#A65C4A]">{value.icon}</div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">{value.title}</h3>
                        <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Why This Conference Matters */}
            <section
              className="py-12 sm:py-20 bg-primary-navy/5"
              style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
            >
              <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 className="text-3xl sm:text-4xl font-bold text-primary-navy mb-4">Why This Conference Matters</h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-primary-orange to-primary-blue mx-auto mb-6"></div>
                </div>
                <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 items-center">
                  <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 border border-primary-navy/10">
                    <div className="space-y-6 sm:space-y-8">
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="p-3 rounded-full bg-primary-navy/10">
                            <TrendingUp className="h-6 w-6 text-primary-orange" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-semibold text-primary-navy mb-2">Economic Impact</h3>
                          <p className="text-sm sm:text-base text-gray-600">
                            Africa's blue economy has the potential to contribute over $400 billion annually by 2030,
                            but requires skilled talent to unlock this potential.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="p-3 rounded-full bg-primary-navy/10">
                            <Handshake className="h-6 w-6 text-primary-orange" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-semibold text-primary-navy mb-2">
                            Knowledge Transfer
                          </h3>
                          <p className="text-sm sm:text-base text-gray-600">
                            Bridging the gap between traditional maritime knowledge and modern technological innovations
                            across African nations.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-shrink-0">
                          <div className="p-3 rounded-full bg-primary-navy/10">
                            <Award className="h-6 w-6 text-primary-orange" />
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg sm:text-xl font-semibold text-primary-navy mb-2">
                            Global Recognition
                          </h3>
                          <p className="text-sm sm:text-base text-gray-600">
                            Positioning African maritime professionals as leaders in the global maritime industry and
                            sustainable ocean management.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="rounded-lg overflow-hidden shadow-lg border border-primary-navy/10">
                      <img
                        src="https://static.africa-press.net/seychelles/sites/30/2023/05/postQueueImg_1683686779.80.jpg"
                        alt="Maritime professionals collaborating"
                        className="w-full h-auto"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-navy/40 to-transparent"></div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Meet Our Team */}
            <section className="py-12 sm:py-20" style={{ backgroundColor: "rgba(255, 255, 255, 0.95)" }}>
              <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-12 sm:mb-16">
                  <h2 className="text-3xl sm:text-4xl font-bold text-primary-navy mb-4">Meet Our Organizers</h2>
                  <div className="h-1 w-24 bg-gradient-to-r from-primary-orange to-primary-blue mx-auto mb-6"></div>
                  <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                    The organizations behind Africa Maritime Conference
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  {/* Ministry of Transport & Logistics Card */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                    <div className="p-6 sm:p-8 flex flex-col items-center">
                      <div className="bg-white rounded-full w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center p-3 mb-6 shadow-md">
                        <img
                          src="/assets/images/logo3.png"
                          alt="Ministry of Transport & Logistics Logo"
                          className="w-24 h-24 sm:w-32 sm:h-32 object-contain"
                        />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-center text-primary-navy mb-3">
                        Ministry of Transport & Logistics, Ethiopia
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 text-center">
                        The Ministry of Transport & Logistics of Ethiopia is responsible for developing and implementing
                        policies that enhance the country's transportation and logistics infrastructure, including
                        maritime services. The Ministry plays a crucial role in positioning Ethiopia as a key maritime
                        hub in East Africa despite being landlocked, through strategic partnerships and development of
                        dry ports.
                      </p>
                    </div>
                  </div>

                  {/* Ethiopian Maritime Training Institute Card */}
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                    <div className="p-6 sm:p-8 flex flex-col items-center">
                      <div className="bg-black rounded-full w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center p-3 mb-6 shadow-md">
                        <img
                          src="/assets/images/escudo.png"
                          alt="Ethiopian Maritime Training Institute Logo"
                          className="w-24 h-24 sm:w-32 sm:h-32 object-contain"
                        />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-center text-primary-navy mb-3">
                        Ethiopian Maritime Training Institute (emtisc.jpg)
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 text-center">
                        The Ethiopian Maritime Training Institute (emtisc.jpg) is a world-class maritime education facility
                        dedicated to training seafarers to international standards. emtisc.jpg provides comprehensive maritime
                        education and training programs that meet STCW requirements, preparing cadets for successful
                        careers in the global maritime industry. The institute has established partnerships with major
                        international shipping companies to ensure employment opportunities for its graduates.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bottom Ad Section - Real Sponsors */}
            <div className="px-4 py-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ethiopian Maritime Training Institute */}
                <InteractiveSponsorCard sponsor={sponsors.about[1]} />

                {/*  Event Management */}
                {/* <InteractiveSponsorCard sponsor={sponsors.about[2]} /> */}
              </div>
            </div>

            {/* Call to Action */}
            <section className="py-12 sm:py-20 bg-gradient-to-r from-primary-navy via-primary-blue to-primary-navy">
              <div className="max-w-4xl mx-auto px-4 text-center">
                <div className="flex justify-center mb-6">
                  <div className="p-4 bg-white/10 rounded-full backdrop-blur-sm border-2 border-[#A65C4A]/30">
                    <Ship className="h-12 w-12 sm:h-16 sm:w-16 text-[#A65C4A]" />
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                  Join the Maritime Revolution
                </h2>
                <div className="h-1 w-24 bg-gradient-to-r from-primary-orange to-white mx-auto mb-6"></div>
                <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                  Be part of Africa's largest maritime conference. Connect with industry leaders, discover
                  opportunities, and shape the future of Africa's blue economy.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center bg-primary-orange hover:bg-primary-orange/90 text-white px-6 py-3 text-lg font-medium rounded-md transition-colors w-auto max-w-[180px] mx-auto sm:mx-0"
                  >
                    Register Now
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white/10 px-6 py-3 text-lg font-medium rounded-md transition-colors w-auto max-w-[180px] mx-auto sm:mx-0"
                  >
                    View Program
                  </Link>
                </div>
                <p className="text-blue-200 mt-6 text-sm"></p>
              </div>
            </section>
          </div>

          {/* Enhanced Sidebar with Real Sponsors */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="lg:sticky z-10 lg:top-[20%] space-y-4 sm:space-y-6">
              {/* Primary Sponsor Banner */}
              <SponsorBanner page="about" />

              {/* Ethiopian Maritime Training Institute Ad */}
              <SponsorAd sponsor={sponsors.about[1]} size="medium" showTagline={true} />

              {/* Conference Info */}
              <div className="bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                <div className="bg-gray-800 px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-300">
                  <h3 className="text-xs sm:text-sm font-bold text-white uppercase tracking-wide">CONFERENCE INFO</h3>
                </div>
                <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                  <div className="text-sm">
                    <span className="font-bold text-gray-900">Date:</span>
                    <span className="text-gray-800 font-semibold ml-2">October 14-16, 2025</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-bold text-gray-900">Location:</span>
                    <span className="text-gray-800 font-semibold ml-2">Addis Ababa, Ethiopia</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-bold text-gray-900">Expected Attendees:</span>
                    <span className="text-gray-800 font-semibold ml-2">500+</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-bold text-gray-900">Languages:</span>
                    <span className="text-gray-800 font-semibold ml-2">English</span>
                  </div>
                </div>
              </div>
              {/* Flawless Event Management Ad */}
              {/* <SponsorAd sponsor={sponsors.about[2]} size="medium" showTagline={true} /> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
